/**
 * Utility functions for handling recurring trainings and classes
 */

/**
 * Parses an end date string (YYYY-MM-DD) and returns a Date object
 * set to the end of that day in local timezone (23:59:59.999)
 * 
 * @param endDateStr - Date string in YYYY-MM-DD format
 * @returns Date object set to end of day in local timezone
 */
export const parseEndDate = (endDateStr: string): Date => {
  const [year, month, day] = endDateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
};

export interface RecurrenceConfig {
  enabled: boolean;
  frequency: 'none' | 'daily' | 'weekly' | 'monthly' | 'annually' | 'weekdays' | 'custom';
  // For weekly and custom:
  daysOfWeek?: number[]; // [1, 3, 5] = Monday, Wednesday, Friday (0 = Sunday)
  // For monthly:
  dayOfMonth?: number; // Day of month (1-31)
  // For annually:
  month?: number; // Month (0-11)
  day?: number; // Day of month
  // Duration:
  endDate?: string; // End date (YYYY-MM-DD format, maximum 3 months)
  occurrences?: number; // Number of occurrences (alternative to endDate)
}

export interface GenerateRecurringDatesParams {
  startDate: Date;
  frequency: RecurrenceConfig['frequency'];
  daysOfWeek?: number[]; // For weekly/custom
  endDate: Date;
  duration: number;
}

/**
 * Generates an array of dates based on recurrence configuration
 */
export const generateRecurringDates = (
  params: GenerateRecurringDatesParams
): Date[] => {
  const { startDate, frequency, daysOfWeek, endDate, duration } = params;
  const dates: Date[] = [];

  // Normalize dates to start of day for comparison (but preserve time from startDate)
  // Use local date methods to avoid timezone issues
  const startDateOnly = new Date(startDate);
  startDateOnly.setHours(0, 0, 0, 0);
  
  const endDateOnly = new Date(endDate);
  endDateOnly.setHours(23, 59, 59, 999); // End of day
  
  // Create date strings using local date components to avoid timezone conversion issues
  const startYear = startDateOnly.getFullYear();
  const startMonth = String(startDateOnly.getMonth() + 1).padStart(2, '0');
  const startDay = String(startDateOnly.getDate()).padStart(2, '0');
  const startDateStr = `${startYear}-${startMonth}-${startDay}`;
  
  const endYear = endDateOnly.getFullYear();
  const endMonth = String(endDateOnly.getMonth() + 1).padStart(2, '0');
  const endDay = String(endDateOnly.getDate()).padStart(2, '0');
  const endDateStr = `${endYear}-${endMonth}-${endDay}`;

  if (frequency === 'daily') {
    let currentDate = new Date(startDate);
    while (currentDate <= endDateOnly) {
      dates.push(new Date(currentDate));
      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else if (frequency === 'weekly' || frequency === 'custom') {
    if (!daysOfWeek || daysOfWeek.length === 0) {
      return dates;
    }

    // Generate all occurrences from startDate to endDate
    // Start from the beginning of the week containing startDate
    let weekStart = new Date(startDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Sunday of the week
    weekStart.setHours(0, 0, 0, 0); // Normalize to start of day
    
    // Calculate how many weeks we need to process
    const daysDiff = Math.ceil((endDateOnly.getTime() - startDateOnly.getTime()) / (24 * 60 * 60 * 1000));
    const weeksToProcess = Math.ceil(daysDiff / 7) + 2; // Add 2 to ensure we cover the end date
    
    // Iterate through all weeks from startDate's week until we've covered endDate
    for (let weekIndex = 0; weekIndex <= weeksToProcess; weekIndex++) {
      for (const dayOfWeek of daysOfWeek) {
        const occurrence = new Date(weekStart);
        occurrence.setDate(occurrence.getDate() + dayOfWeek);
        
        // Preserve the time from startDate
        occurrence.setHours(
          startDate.getHours(),
          startDate.getMinutes(),
          startDate.getSeconds(),
          startDate.getMilliseconds()
        );
        
        // Use date-only comparison to avoid timezone issues
        // Use local date components instead of toISOString to avoid timezone conversion
        const occurrenceDateOnly = new Date(occurrence);
        occurrenceDateOnly.setHours(0, 0, 0, 0);
        const occYear = occurrenceDateOnly.getFullYear();
        const occMonth = String(occurrenceDateOnly.getMonth() + 1).padStart(2, '0');
        const occDay = String(occurrenceDateOnly.getDate()).padStart(2, '0');
        const occurrenceDateStr = `${occYear}-${occMonth}-${occDay}`;
        
        // Include if it's within the date range (including startDate and endDate)
        // Use string comparison to avoid timezone issues
        if (occurrenceDateStr >= startDateStr && occurrenceDateStr <= endDateStr) {
          dates.push(new Date(occurrence));
        }
      }
      // Move to next week
      weekStart.setDate(weekStart.getDate() + 7);
    }
  } else if (frequency === 'weekdays') {
    let currentDate = new Date(startDate);
    while (currentDate <= endDateOnly) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        // Monday to Friday
        dates.push(new Date(currentDate));
      }
      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else if (frequency === 'monthly') {
    let currentDate = new Date(startDate);
    while (currentDate <= endDateOnly) {
      dates.push(new Date(currentDate));
      currentDate = new Date(currentDate);
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  } else if (frequency === 'annually') {
    let currentDate = new Date(startDate);
    while (currentDate <= endDateOnly) {
      dates.push(new Date(currentDate));
      currentDate = new Date(currentDate);
      currentDate.setFullYear(currentDate.getFullYear() + 1);
    }
  }

  // Sort dates
  return dates.sort((a, b) => a.getTime() - b.getTime());
};

/**
 * Calculates the number of occurrences based on recurrence configuration
 * Filters out dates that have already passed (same logic as in AddClassModal/AddTrainingModal)
 * Uses the same date generation and filtering logic as when actually creating the classes/trainings
 * 
 * @param config - Recurrence configuration
 * @param startDate - Start date for the recurrence
 * @param now - Optional current date/time (for testing). If not provided, uses new Date()
 */
export const calculateOccurrences = (
  config: RecurrenceConfig,
  startDate?: Date,
  now?: Date
): number => {
  if (!startDate || !config.endDate) return 0;

  // Parse endDate from YYYY-MM-DD string and set to end of day in local timezone
  const endDate = parseEndDate(config.endDate);
  
  // Generate all recurring dates (this includes dates starting from startDate)
  // Note: generateRecurringDates will generate dates based on the recurrence pattern,
  // which may start from a date after startDate if startDate doesn't match the pattern
  // For example, if startDate is Monday but recurrence is "Weekly on Wednesday",
  // it will generate the first Wednesday on or after startDate
  const dates = generateRecurringDates({
    startDate,
    frequency: config.frequency,
    daysOfWeek: config.daysOfWeek,
    endDate,
    duration: 60, // Not important for counting
  });

  // Filter out dates that have already passed (for today, only include if time hasn't passed)
  // This matches the EXACT logic used when creating classes/trainings in AddClassModal/AddTrainingModal
  // Copy-pasted from AddTrainingModal.tsx lines 727-738
  const currentTime = now || new Date();
  const today = new Date(currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate());
  const filteredDates = dates.filter(date => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    // If it's today, check if the time has passed
    if (dateOnly.getTime() === today.getTime()) {
      return date > currentTime; // Only include if the time hasn't passed
    }
    // For future dates, always include
    return date > currentTime;
  });

  return filteredDates.length;
};

/**
 * Validates recurrence configuration
 */
export const validateRecurrenceConfig = (
  config: RecurrenceConfig,
  startDate?: Date,
  maxMonths: number = 3
): { isValid: boolean; error?: string } => {
  if (!config.enabled || config.frequency === 'none') {
    return { isValid: true };
  }

  if (!startDate) {
    return { isValid: false, error: 'Start date is required for recurrence' };
  }

  if (!config.endDate) {
    return { isValid: false, error: 'End date is required for recurrence' };
  }

  // Parse end date as date-only (YYYY-MM-DD) to avoid timezone issues
  const [endYear, endMonth, endDay] = config.endDate.split('-').map(Number);
  const endDateOnly = new Date(endYear, endMonth - 1, endDay);
  
  // Normalize startDate to date-only (start of day) for comparison
  const startDateOnly = new Date(startDate);
  startDateOnly.setHours(0, 0, 0, 0);
  
  // Calculate max end date (startDate + maxMonths)
  const maxEndDate = new Date(startDateOnly);
  maxEndDate.setMonth(maxEndDate.getMonth() + maxMonths);
  maxEndDate.setHours(23, 59, 59, 999); // End of the max month day

  // Compare dates using date-only (ignore time)
  const endDateOnlyNormalized = new Date(endYear, endMonth - 1, endDay);
  endDateOnlyNormalized.setHours(0, 0, 0, 0);
  
  const maxEndDateOnly = new Date(maxEndDate);
  maxEndDateOnly.setHours(0, 0, 0, 0);

  if (endDateOnlyNormalized > maxEndDateOnly) {
    return {
      isValid: false,
      error: `Recurrence cannot exceed ${maxMonths} months from the start date`,
    };
  }

  // Check if end date is before or equal to start date
  // Use date-only comparison to avoid timezone issues
  if (endDateOnlyNormalized < startDateOnly) {
    return { 
      isValid: false, 
      error: 'End date must be on or after the start date' 
    };
  }

  if (
    (config.frequency === 'weekly' || config.frequency === 'custom') &&
    (!config.daysOfWeek || config.daysOfWeek.length === 0)
  ) {
    return {
      isValid: false,
      error: 'Please select at least one day of the week',
    };
  }

  return { isValid: true };
};

